/**
 * Stable identity for every node in a score.
 *
 * Every part, staff, measure, voice, event, direction and spanner carries an id that is
 * created once and never reused. Ids are what make the rest of the editor possible: undo
 * refers to nodes by id rather than by position, a spanner names the two events it runs
 * between, the renderer maps a clicked SVG element back to the node it was drawn from, and
 * an outside analyser can anchor a remark to the exact note it is about. Positional
 * addressing -- "the third note of bar four" -- breaks the moment anything is inserted, so
 * nothing in this package uses it.
 *
 * The ids are branded so that a `PartId` cannot be passed where a `MeasureId` is wanted.
 * The brand exists only in the type system; at runtime an id is an ordinary string, which
 * keeps the whole document JSON-serialisable without a reviver.
 */

/** A string tagged with the kind of node it names. */
export type Id<Kind extends string> = string & { readonly __idKind: Kind };

export type ScoreId = Id<"score">;
export type PartId = Id<"part">;
export type StaffId = Id<"staff">;
export type MeasureId = Id<"measure">;
export type VoiceId = Id<"voice">;
export type EventId = Id<"event">;
export type DirectionId = Id<"direction">;
export type SpannerId = Id<"spanner">;
export type GroupId = Id<"group">;
export type HarmonyId = Id<"harmony">;
export type FigureId = Id<"figure">;

/** Every id kind this package mints, for exhaustiveness at the call sites. */
export type IdKind =
  | "score"
  | "part"
  | "staff"
  | "measure"
  | "voice"
  | "event"
  | "direction"
  | "spanner"
  | "group"
  | "harmony"
  | "figure";

let counter = 0;

/**
 * Mint a fresh id of the given kind.
 *
 * The shape is `<kind>-<random>-<counter>`: the prefix makes a document readable while
 * debugging, the random part keeps ids from colliding when two documents are merged or
 * when a part is copied between scores, and the counter keeps them unique within one
 * session even if the random source repeats.
 *
 * `crypto.randomUUID` is deliberately not used. It is missing on insecure origins and in
 * older runtimes, and an editor that cannot create a note over plain HTTP is not worth the
 * uniqueness guarantee. Ids are never a security boundary here.
 */
export function createId<Kind extends IdKind>(kind: Kind): Id<Kind> {
  counter += 1;
  const random = Math.random().toString(36).slice(2, 10);
  return `${kind}-${random}-${counter.toString(36)}` as Id<Kind>;
}

/** Read the kind back out of an id, or `null` when the string is not one of ours. */
export function idKind(id: string): IdKind | null {
  const prefix = id.slice(0, id.indexOf("-"));
  return KINDS.has(prefix) ? (prefix as IdKind) : null;
}

const KINDS: ReadonlySet<string> = new Set<IdKind>([
  "score",
  "part",
  "staff",
  "measure",
  "voice",
  "event",
  "direction",
  "spanner",
  "group",
  "harmony",
  "figure",
]);

/**
 * Reset the session counter. Only for deterministic snapshots in tooling; ids minted
 * after a reset can collide with ids minted before it, so no editing path may call this.
 */
export function resetIdCounter(): void {
  counter = 0;
}
